import { createRootRouteWithContext, Outlet, useMatches, useRouter } from "@tanstack/react-router";
import { HIGHARC_DOMAIN } from "common/constants/env";
import Flags from "common/constants/flags";
import { SITE_DESCRIPTION } from "common/constants/marketing";
import { shouldAttemptChunkReload, showPersistentReloadNotification } from "common/utils/chunkReloadGuard";
import { initPosthog, posthog } from "lib/analytics";
import { Suspense, useEffect, useRef } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import "ui/styles/global/index.scss";
import AppContainer from "../components/AppContainer";
import { startDeploymentPolling } from "../src/deploymentRefresh";
import type { AppRouterContext } from "../src/routerContext";
import { detectAndReportShellSkew, scheduleStaticCacheAudit } from "../src/spaDeployDiagnostics";

// Throttled build ID check — returns true if the live deployment differs from
// the build currently running in the browser. Always emits spa.shell_skew RUM
// telemetry on mismatch (independent of the client-reload feature flag).
let lastVersionCheckTime = 0;
const VERSION_CHECK_INTERVAL = 30_000; // 30 seconds

async function hasNewDeployment(reason: string): Promise<boolean> {
  const now = Date.now();
  if (now - lastVersionCheckTime < VERSION_CHECK_INTERVAL) {
    return false;
  }
  lastVersionCheckTime = now;

  const clientBuildId = process.env.PUBLIC_BUILD_ID;
  if (!clientBuildId) {
    return false;
  }

  const { skewed } = await detectAndReportShellSkew({ clientBuildId, reason });
  return skewed;
}

export const Route = createRootRouteWithContext<AppRouterContext>()({
  // Check for new deployments before loading child route chunks
  beforeLoad: async ({ location }) => {
    if ((await hasNewDeployment(`route-load:${location.href}`)) && shouldAttemptChunkReload()) {
      showPersistentReloadNotification(`Detected deployment change during route load for ${location.href}`);
    }
  },
  component: RootLayout,
});

function RootLayout() {
  const router = useRouter();
  const isPublicPage = useMatches({
    select: (matches) => matches.some((match) => match.staticData?.isPublicPage === true),
  });
  const posthogInitialized = useRef(false);

  // Initialize PostHog and track page views (only on path change, not query params)
  const lastPathRef = useRef<string | null>(null);
  useEffect(() => {
    if (!posthogInitialized.current) {
      posthogInitialized.current = true;
      void initPosthog();
    }
    const unsubscribe = router.subscribe("onResolved", ({ toLocation }) => {
      if (toLocation.pathname !== lastPathRef.current) {
        lastPathRef.current = toLocation.pathname;
        posthog.capture("$pageview");
      }
    });
    return unsubscribe;
  }, [router]);

  // Poll for new deployments when the user is idle (not navigating)
  useEffect(() => {
    const clientBuildId = process.env.PUBLIC_BUILD_ID;
    if (!clientBuildId) {
      return;
    }

    const cacheSuffix = Flags.GIT_COMMIT_HASH || clientBuildId;
    const stopStaticCacheAudit = scheduleStaticCacheAudit(clientBuildId, cacheSuffix);

    // best-effort immediate check: this may no-op if beforeLoad just updated throttle state
    const stopPolling = startDeploymentPolling({
      checkForNewDeployment: () => hasNewDeployment("periodic-version-check"),
      onNewDeployment: () => {
        if (shouldAttemptChunkReload()) {
          showPersistentReloadNotification("Detected deployment change during periodic version check");
        }
      },
    });

    // Browsers throttle setInterval in backgrounded tabs and pause it entirely
    // while the laptop is asleep, so the 5-min poll can miss overnight deploys.
    // Force a check the moment the tab becomes visible again.
    const onVisibilityChange = async () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      lastVersionCheckTime = 0;
      if ((await hasNewDeployment("tab-visibility-restore")) && shouldAttemptChunkReload()) {
        showPersistentReloadNotification("Detected deployment change after tab visibility restore");
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopPolling();
      stopStaticCacheAudit();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return (
    <HelmetProvider>
      <Helmet>
        <meta name="description" content={SITE_DESCRIPTION} />
        <meta property="og:title" content="Higharc" />
        <meta property="og:description" content={SITE_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={HIGHARC_DOMAIN} />
        <meta property="og:image" content="/static/og-image.png" />
        <meta property="twitter:image" content="/static/og-image.png" />
      </Helmet>
      <AppContainer isPublicPage={isPublicPage}>
        <Suspense>
          <Outlet />
        </Suspense>
      </AppContainer>
    </HelmetProvider>
  );
}
